import API from 'src/api'
import utils from 'src/utils'
import { http } from '../boot/axios'
import { Capacitor } from '@capacitor/core'

// El interceptor de boot/axios.js ya sube el piso a 120s para requests con responseType
// blob/arraybuffer, pero estos reportes se generan sincronos en el servidor: dompdf solo
// tarda ~160s con miles de ubicaciones, mas que ese piso. `getReport` ademas puede pedir
// `format: 'json'`, que el interceptor no cubre. 300s: mismo orden que PULL_TIMEOUT de
// offline/sync/pull/catalogs.js, el otro caso conocido de generacion/transferencia pesada.
const REPORTS_CONFIG = { timeout: 300000 }

// Limite practico de querystring antes de que el edge rechace la peticion (el rechazo
// llega sin headers CORS, asi que el navegador lo reporta como "CORS error" y no como el
// 4xx/5xx que es). Medido contra graphql.binnacle.mx: ~10KB de URL. Se deja margen.
const MAX_URL_QUERY = 6000

// Unico reporte cuyo endpoint acepta POST ademas de GET. Cualquier otro reportType sigue
// por el camino de siempre.
const EXHIBITIONS_REPORT = 'exhibitions'

// Algunos callers (stores/reports/actions.js) ya mandan forms/locations como CSV;
// otros (NewReportsHeader.vue) pasan el array crudo del store. El Array.isArray
// evita re-unir un string que ya llego serializado.
function serializeReportPayload (payload, fields) {
  const serialized = { ...payload }
  fields.forEach(field => {
    if (Array.isArray(payload[field])) {
      serialized[field] = payload[field].join(',')
    }
  })
  return serialized
}

export default {
  URL: `${API.URL}reports`,
  GRAPHQL_URL: `${API.GRAPHQL_URL}reports`,

  async getReport (payload) {
    let newReports = ['caducity', 'incidences', 'photos', 'management_report', 'tasks', 'follow_up',
      'answers',
      'dimsa_rac', 'power_factor', 'agenda_report', 'form_by_scale', 'fullfillment', 'general',
      'cash', 'self_evaluation',
      'workday', 'calimax_workday', 'auditor_role', 'unified_incidences', 'resources', 'expiration',
      'campaigns',
      'extras', 'non_executions_report', 'tables_report', 'forensic_audit', 'planogram_report',
      'semaphore', 'critical_points',
      'planogram_quality_report', 'planogram_table_report', 'consolidated_answers', 'sprints', 'user', 'daily', 'execution_time',
      'category', 'category_toks', 'responsability_leadership', 'consolided', 'check_in', 'satisfaction_surveys',
      'sprint_gallery', 'exhibitions', 'attendance'
    ]

    let url = newReports.includes(payload.query.reportType) ? this.GRAPHQL_URL : this.URL

    try {
      // payload.requestConfig deja a quien llama ajustar la peticion (timeout, avisos) sin tocar a los demas reportes.
      const requestConfig = { ...REPORTS_CONFIG, ...payload.requestConfig }
      if (url === this.GRAPHQL_URL) {
        requestConfig.responseType = payload.query.format !== 'json' ? 'blob' : 'json'
      }

      // Con selecciones grandes (p. ej. "todas" las ubicaciones en organizaciones con
      // miles de sucursales) la querystring se dispara por arriba del limite que acepta
      // el edge y la peticion se rechaza antes de llegar al backend. En esos casos se
      // manda por POST con los mismos parametros en el body (URL corta). El resto de los
      // reportes conserva el GET de siempre. Solo aplica al backend GraphQL, que es el que
      // acepta ambos metodos en esta ruta.
      const asQueryString = new URLSearchParams(payload.query).toString()
      const usesPost = url === this.GRAPHQL_URL && asQueryString.length > MAX_URL_QUERY

      let response
      if (usesPost) {
        response = await http.post(`${url}/new`, payload.query, requestConfig)
      } else {
        requestConfig.params = payload.query
        response = await http.get(`${url}/new`, requestConfig)
      }

      let fileName = `${payload.reportName}`
      let blobType = ''

      if (payload.query.format === 'pdf') {
        fileName += '.pdf'
        blobType = 'application/pdf'
      } else if (payload.query.format === 'xlsx') {
        fileName += '.xlsx'
        blobType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }

      if (blobType) {
        if (Capacitor.isNativePlatform()) {
          utils.download(fileName, response.data, blobType)
        } else {
          const { saveAs } = await import('file-saver')
          saveAs(new Blob([response.data], { type: blobType }), fileName)
        }
      } else {
        return response.data
      }
    } catch (err) {
      console.error('❌ Error al obtener el reporte:', err)
      throw err
    }
  },
  getOccurrences (query) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/details`, { params: query })
        .then(response => resolve(response.data.data))
        .catch(err => reject(err))
    })
  },
  getItemDetails (query) {
    return http.get(
      `${API.GRAPHQL_URL}item_tracing/${query.item_id}`,
      { params: query }
    )
  },
  downloadCategoryReport (payload, reportName) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/category`, payload, {
        ...REPORTS_CONFIG,
        responseType: 'blob'
      })
        .then(async response => {
          if (payload.format === 'pdf') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.pdf`, response.data, 'application/pdf')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/pdf' }), `${reportName}.pdf`)
            }
          }
          if (payload.format === 'xlsx') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.xlsx`, response.data, 'application/vnd.ms-excel')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/vnd.ms-excel' }), `${reportName}.xlsx`)
            }
          }
          resolve()
        })
        .catch(err => reject(err))
    })
  },
  downloadWorkdayReport (payload, reportName) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/workday`, payload, {
        ...REPORTS_CONFIG,
        responseType: 'blob'
      })
        .then(async response => {
          if (payload.format === 'pdf') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.pdf`, response.data, 'application/pdf')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/pdf' }), `${reportName}.pdf`)
            }
          }
          if (payload.format === 'xlsx') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.xlsx`, response.data, 'application/vnd.ms-excel')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/vnd.ms-excel' }), `${reportName}.xlsx`)
            }
          }
          resolve()
        })
        .catch(err => reject(err))
    })
  },
  downloadBoardReport (payload, reportName) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/board`, payload, {
        ...REPORTS_CONFIG,
        responseType: 'blob'
      })
        .then(async response => {
          if (payload.format === 'pdf') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.pdf`, response.data, 'application/pdf')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/pdf' }), `${reportName}.pdf`)
            }
          }
          if (payload.format === 'xlsx') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.xlsx`, response.data, 'application/vnd.ms-excel')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/vnd.ms-excel' }), `${reportName}.xlsx`)
            }
          }
          resolve()
        })
        .catch(err => reject(err))
    })
  },
  downloadTaskReport (payload, reportName) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/taskPDFGraphics`, payload, {
        ...REPORTS_CONFIG,
        responseType: 'blob'
      })
        .then(async response => {
          if (response.data.type === 'application/pdf') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.pdf`, response.data, 'application/pdf')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/pdf' }), `${reportName}.pdf`)
            }
          }
          if (payload.query.format === 'xlsx') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.xlsx`, response.data, 'application/vnd.ms-excel')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/vnd.ms-excel' }), `${reportName}.xlsx`)
            }
          }
          resolve()
        })
        .catch(err => reject(err))
    })
  },
  downloadAnswersReport (payload, reportName) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/answers`, payload, {
        ...REPORTS_CONFIG,
        responseType: 'blob'
      })
        .then(async response => {
          if (payload.format === 'pdf') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.pdf`, response.data, 'application/pdf')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/pdf' }), `${reportName}.pdf`)
            }
          }
          if (payload.format === 'xlsx') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.xlsx`, response.data, 'application/vnd.ms-excel')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/vnd.ms-excel' }), `${reportName}.xlsx`)
            }
          }
          resolve()
        })
        .catch(err => reject(err))
    })
  },
  downloadFollowUpReport (payload, reportName) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/followUp`, payload, {
        ...REPORTS_CONFIG,
        responseType: 'blob'
      })
        .then(async response => {
          if (payload.format === 'pdf') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.pdf`, response.data, 'application/pdf')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/pdf' }), `${reportName}.pdf`)
            }
          }
          if (payload.format === 'xlsx') {
            if (Capacitor.isNativePlatform()) {
              utils.download(`${reportName}.xlsx`, response.data, 'application/vnd.ms-excel')
            } else {
              const { saveAs } = await import('file-saver')
              saveAs(new Blob([response.data], { type: 'application/vnd.ms-excel' }), `${reportName}.xlsx`)
            }
          }
          resolve()
        })
        .catch(err => reject(err))
    })
  },
  sendReportByEmail (payload) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/share`, payload)
        .then(res => { resolve(res.data) })
        .catch(err => { reject(err) })
    })
  },
  downloadSprintExcelReport (emailList, locations, sprintId) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/sprintsExcelEmail`, { emailList, locations, sprintId })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          console.error('Error en la exportación:', error)
          reject(error)
        })
    })
  },
  downloadSprintExcelReportDirect (locations, sprintId) {
    const params = { locations, sprintId }
    return http.post(`${this.GRAPHQL_URL}/sprintsExcelDownload`, params, {
      ...REPORTS_CONFIG,
      responseType: 'blob'
    })
  },
  downloadExcelReportDirect (payload, reportType) {
    // Exhibiciones sale por su propia ruta antes de tocar nada de lo de abajo: es el unico
    // reporte cuyo endpoint acepta POST, y el resto conserva su GET original sin cambios.
    if (reportType === EXHIBITIONS_REPORT) {
      return this.downloadExhibitionsExcelDirect(payload)
    }

    console.log('ulises', payload, reportType)
    const endpointMap = {
      'sprint': '/sprintsExcelDownload',
      'agenda_report': '/agendaExcelDownload',
      'workday': '/workdayExcelDownload',
      'exhibitions': '/exhibitionsExcelDownload',
      'user': payload.reportTypeChild === 'detail' ? '/userDetailsExcelDownload' : '/userExcelDownload'
    }
    const endpoint = endpointMap[reportType]
    const cleanPayload = {
      ...payload,
      locations: Array.isArray(payload.locations) ? payload.locations.join(',') : payload.locations,
      users: Array.isArray(payload.users) ? payload.users.join(',') : payload.users
    }

    return http.get(`${this.GRAPHQL_URL}${endpoint}`, {
      ...REPORTS_CONFIG,
      params: cleanPayload,
      responseType: 'blob'
    })
  },
  // El Reporte de Exhibiciones manda la seleccion de ubicaciones en la querystring. Con
  // "todas" en una organizacion grande la URL pasa de 25KB y el edge la rechaza antes de
  // llegar al backend; como ese rechazo no trae headers CORS, el navegador lo reporta como
  // "CORS error" en vez del 5xx que es. Medido contra graphql.binnacle.mx: el preflight pasa
  // con 9,749 bytes de URL y falla con 10,949. Arriba del umbral la lista viaja en el body.
  downloadExhibitionsExcelDirect (payload) {
    const cleanPayload = this.exhibitionsPayload(payload)
    const requestConfig = { ...REPORTS_CONFIG, responseType: 'blob' }

    if (this.exceedsUrlLimit(cleanPayload)) {
      return http.post(`${this.GRAPHQL_URL}/exhibitionsExcelDownload`, cleanPayload, requestConfig)
    }

    return http.get(`${this.GRAPHQL_URL}/exhibitionsExcelDownload`, {
      ...requestConfig,
      params: cleanPayload
    })
  },
  sendExhibitionsExcelByEmail (emailList, payload) {
    const cleanPayload = { ...this.exhibitionsPayload(payload), emailList: emailList.join(',') }

    if (this.exceedsUrlLimit(cleanPayload)) {
      return http.post(`${this.GRAPHQL_URL}/exhibitionsExcelEmail`, cleanPayload)
    }

    return http.get(`${this.GRAPHQL_URL}/exhibitionsExcelEmail`, { params: cleanPayload })
  },
  exhibitionsPayload (payload) {
    return {
      ...payload,
      locations: Array.isArray(payload.locations) ? payload.locations.join(',') : payload.locations,
      users: Array.isArray(payload.users) ? payload.users.join(',') : payload.users
    }
  },
  exceedsUrlLimit (cleanPayload) {
    // Se replica como lo serializaria axios en `params` (JSON para objetos) para medir la
    // URL real que saldria, no una aproximacion optimista.
    const flat = Object.keys(cleanPayload).reduce((acc, key) => {
      const value = cleanPayload[key]
      if (value === null || value === undefined) return acc
      acc[key] = typeof value === 'object' ? JSON.stringify(value) : String(value)
      return acc
    }, {})

    return new URLSearchParams(flat).toString().length > MAX_URL_QUERY
  },
  sendExcelReportByEmail (emailList, payload, reportType) {
    if (reportType === EXHIBITIONS_REPORT) {
      return new Promise((resolve, reject) => {
        this.sendExhibitionsExcelByEmail(emailList, payload)
          .then(response => resolve(response.data))
          .catch(error => {
            console.error(`Error enviando reporte por email (${reportType}):`, error)
            reject(error)
          })
      })
    }

    const endpointMap = {
      sprints: '/sprintsExcelEmail',
      agenda_report: '/agendaExcelEmail',
      administration_report: '/administrationExcelEmail',
      user_tracking_report: '/userTrackingExcelEmail',
      workday: '/workdayExcelEmail',
      exhibitions: '/exhibitionsExcelEmail',
      incidences: payload.reportTypeChild === 'by_location' ? '/incidencesByLocationExcelEmail' : '/incidencesExcelEmail',
      general: '/dbGeneralReportEmail',
      user: payload.reportTypeChild === 'detail' ? '/userDetailsExcelEmail' : '/userExcelEmail'
    }

    const endpoint = endpointMap[reportType]

    const cleanPayload = {
      ...payload,
      forms: reportType === 'general' && Array.isArray(payload.forms) ? payload.forms.join(',') : payload.forms,
      locations: Array.isArray(payload.locations) ? payload.locations.join(',') : payload.locations,
      users: Array.isArray(payload.users) ? payload.users.join(',') : payload.users,
      emailList: emailList.join(',')
    }

    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}${endpoint}`, {
        params: cleanPayload
      })
        .then(response => resolve(response.data))
        .catch(error => {
          console.error(`Error enviando reporte por email (${reportType}):`, error)
          reject(error)
        })
    })
  },
  // El PDF del Reporte General con miles de ubicaciones no aguanta el render
  // sincrono (dompdf solo, sin la peticion HTTP, tarda ~160s y pico ~2.1GB de RAM
  // con ~3,300 ubicaciones). Va siempre por POST,
  // nunca por GET: la lista de ubicaciones en la querystring es justo lo que
  // ya reboto el edge antes de que esto se moviera a un job async.
  sendGeneralReportPdfByEmail (emailList, payload) {
    const cleanPayload = {
      ...serializeReportPayload(payload, ['forms', 'locations', 'users']),
      emailList: emailList.join(',')
    }

    return http.post(`${this.GRAPHQL_URL}/dbGeneralReportPdfEmail`, cleanPayload)
  },
  // "Descarga Local" del raw del Reporte General: mismo job de exports-heavy
  // que el envio por correo, pero el backend entrega el link por el panel de
  // notificaciones (campanita + push) al usuario que lo pidio.
  requestGeneralReportByNotification (payload) {
    const cleanPayload = {
      ...serializeReportPayload(payload, ['forms', 'locations', 'users']),
      delivery: 'notification'
    }

    return http.get(`${this.GRAPHQL_URL}/dbGeneralReportEmail`, { params: cleanPayload })
  },
  requestPhotosReportByNotification (payload) {
    const cleanPayload = {
      ...payload,
      forms: Array.isArray(payload.forms) ? payload.forms.join(',') : payload.forms,
      locations: Array.isArray(payload.locations) ? payload.locations.join(',') : payload.locations,
      delivery: 'notification'
    }

    return http.post(`${this.GRAPHQL_URL}/photosReportPdfEmail`, cleanPayload)
  },
  sendPhotosReportByEmail (emailList, payload) {
    const cleanPayload = {
      ...payload,
      forms: Array.isArray(payload.forms) ? payload.forms.join(',') : payload.forms,
      locations: Array.isArray(payload.locations) ? payload.locations.join(',') : payload.locations,
      emailList: emailList.join(','),
      delivery: 'email'
    }

    return http.post(`${this.GRAPHQL_URL}/photosReportPdfEmail`, cleanPayload)
  },
  // PDF del Reporte General en segundo plano con seguimiento: responde 202 con
  // el id de la exportacion y el avance se consulta con getReportExportStatus.
  // Va por POST y no por GET: la lista de ubicaciones de una seleccion completa
  // no cabe en la querystring y el edge rechaza la peticion antes del backend.
  startGeneralPdfExport (payload) {
    return http.post(`${this.GRAPHQL_URL}/general/pdf-exports`, serializeReportPayload(payload, ['forms', 'locations', 'users']))
  },
  getReportExportStatus (exportJobId) {
    return http.get(`${this.GRAPHQL_URL}/exports/${exportJobId}`)
  },
  // Los acuses le dicen al backend que el usuario ya vio el resultado con el
  // dialogo abierto, para que no mande la notificacion diferida.
  ackReportExport (exportJobId) {
    return http.post(`${this.GRAPHQL_URL}/exports/${exportJobId}/ack`)
  },
  ackReportExportFailure (exportJobId) {
    return http.post(`${this.GRAPHQL_URL}/exports/${exportJobId}/ack-failure`)
  },
  // La URL llega completa desde el backend (link del token de descarga).
  downloadReportExportFile (url) {
    return http.get(url, { responseType: 'blob' })
  }
}
